import { createContext, useContext, useEffect, useState } from "react";

const translations = {
  de: {
    nav: {
      features: "Funktionen",
      pricing: "Preise",
      login: "Anmelden",
      register: "Jetzt registrieren",
      dashboard: "Dashboard",
      sites: "Baustellen",
      query: "Standortabfrage",
      profile: "Profil",
      billing: "Abo & Rechnung",
      logout: "Abmelden",
    },
    landing: {
      hero_eyebrow: "Standortdaten für den GaLaBau",
      hero_title: "Standortdaten in Sekunden — nicht in Stunden.",
      hero_subtitle:
        "Klima, Bodendaten und Winterhärtezonen für jede Adresse in der DACH-Region. Bereit für die nächste Baustelle, fertig als PDF.",
      hero_cta_primary: "Mit Messe-Code starten",
      hero_cta_secondary: "Funktionen ansehen",
      fair_badge: "Exklusiver Messe-Test",
      fair_note:
        "Der Zugang ist aktuell exklusiv für Messebesucher: Mit dem Code von der Messe registrieren und das Paket Free freischalten.",
      trust: "Vertraut von Landschaftsarchitekten & GaLa-Bauern",
      features_title: "Alles, was Sie für jede Baustelle wissen müssen.",
      features_subtitle:
        "Von der ersten Standortanalyse bis zum Kundenbericht — alle Daten an einem Ort.",
      f1_title: "Klimadaten 10-Jahres-Mittel",
      f1_desc:
        "Niederschlag, Temperatur, Sonnenstunden, Frosttage — basierend auf historischen Wetterstationsdaten.",
      f2_title: "Winterhärtezone (USDA)",
      f2_desc:
        "Berechnet aus 30 Jahren ERA5-Land-Daten (~9 km) über die Normalperiode 1991–2020 — nach offizieller USDA-Methodik.",
      f3_title: "Bodendaten ISRIC SoilGrids",
      f3_desc: "Bodenart nach USDA-Texturdreieck, pH, Humus, Nährstoffspeicher (KAK), Grobboden, nutzbare Feldkapazität — plus geologischer Untergrund.",
      f4_title: "Baustellen-Verwaltung",
      f4_desc: "Speichern Sie Standorte mit allen Daten — pro Projekt, pro Kunde, pro Region.",
      f5_title: "Professionelles PDF",
      f5_desc:
        "Ein-Klick-Export Ihrer kompletten Standortdaten — mit Ihrem Firmenlogo und allen Diagrammen.",
      f6_title: "Geocoding & Karte",
      f6_desc: "Adresssuche oder GPS-Koordinaten — visualisiert auf einer interaktiven Karte.",
      cta_title: "Bereit für die nächste Baustelle?",
      cta_subtitle: "Exklusiver Messe-Test — Registrierung mit Ihrem Messe-Code, danach steht Ihnen das Paket Free zur Verfügung.",
      cta_button: "Jetzt loslegen",
    },
    auth: {
      login_title: "Willkommen zurück",
      login_subtitle: "Bei Ihrem BaugrundCheck.de-Konto anmelden",
      register_title: "Konto erstellen",
      register_subtitle: "Exklusiver Messe-Test — Registrierung mit Messe-Code",
      email: "E-Mail",
      password: "Passwort",
      name: "Name",
      company: "Firma (optional)",
      access_code: "Messe-Code",
      access_code_placeholder: "Code von der Messe",
      access_code_hint: "Den Code haben Sie an unserem Messestand erhalten. Damit schalten Sie das Paket Free frei.",
      login_button: "Anmelden",
      register_button: "Konto erstellen",
      no_account: "Noch kein Konto?",
      have_account: "Bereits ein Konto?",
      to_register: "Jetzt registrieren",
      to_login: "Hier anmelden",
    },
    dashboard: {
      welcome: "Willkommen zurück",
      subtitle: "Ihre Standortdaten-Übersicht",
      kpi_sites: "Baustellen",
      kpi_queries: "Standortabfragen",
      kpi_plan: "Aktueller Plan",
      cta_query: "Neue Standortabfrage",
      cta_sites: "Alle Baustellen",
      recent: "Letzte Baustellen",
      no_sites: "Noch keine Baustellen angelegt.",
      create_first: "Erste Baustelle anlegen",
      quota_left: "noch {n} frei",
      quota_exhausted: "Kontingent aufgebraucht",
    },
    query: {
      title: "Standortabfrage",
      subtitle: "Adresse oder Koordinaten eingeben für komplette Standortdaten.",
      tab_address: "Adresse",
      tab_coords: "Koordinaten",
      tab_geo: "Meinen Standort",
      geo_hint: "Klicken Sie auf den Button — Ihr Browser fragt nach der Standortberechtigung.",
      geo_button: "Aktuellen Standort verwenden",
      geo_locating: "Standort wird ermittelt…",
      geo_denied: "Standortzugriff wurde verweigert oder ist nicht verfügbar.",
      geo_unavailable: "Geolokalisierung wird von diesem Browser nicht unterstützt.",
      address_placeholder: "z.B. Musterstraße 12, 80331 München",
      lat: "Breitengrad (Lat)",
      lng: "Längengrad (Lng)",
      search: "Abfragen",
      save_site: "Als Baustelle speichern",
      download_pdf: "PDF-Bericht herunterladen",
      loading: "Lade Standortdaten…",
      no_result: "Adresse nicht gefunden.",
      section_overview: "Übersicht",
      section_climate: "Klimadaten",
      section_hardiness: "Winterhärtezone",
      section_soil: "Bodendaten",
      section_geology: "Geologischer Untergrund",
      coords: "Koordinaten",
      elevation: "Höhe ü. NN",
      country: "Land",
      state: "Region",
      annual_temp: "Ø Temperatur",
      annual_precip: "Niederschlag/Jahr",
      annual_sun: "Sonnenstunden/Jahr",
      frost_days: "Frosttage/Jahr",
      ph: "pH-Wert",
      texture: "Bodentyp",
      sand: "Sand",
      silt: "Schluff",
      clay: "Ton",
      organic_carbon: "Organ. Kohlenstoff",
      humus: "Humus",
      cec: "KAK (cmol/kg)",
      nitrogen: "Gesamtstickstoff",
      coarse_fragments: "Grobboden/Skelett",
      field_capacity: "Feldkapazität",
      available_water: "Nutzbare Feldkap.",
      bulk_density: "Lagerungsdichte",
      hardiness_label: "USDA-Zone",
      hardiness_range: "Temperaturbereich",
      hardiness_min: "Planungswert (kälteste Winter)",
      hardiness_usda: "USDA-Mittel (alle Jahre)",
      hardiness_lowest: "Tiefste Wintertemperatur",
      hardiness_fallback:
        "Ersatzquelle mit gröberem Raster verwendet — Zone kann in gegliedertem Gelände abweichen.",
      source: "Quelle",
      years: "Jahre",
      lithology: "Gestein",
      geo_unit: "Kartiereinheit",
      geo_period: "Zeitalter",
      geo_environment: "Ablagerungsmilieu",
      geo_note: "Hinweis",
      soil_unavailable: "Bodendaten nicht verfügbar",
      association_title: "Bodenkundliche Einheit",
      association_note:
        "Übersichtskarte ohne Messwerte — sie beschreibt den vorherrschenden Bodentyp der Umgebung, ersetzt aber keine Rasterdaten und keine Bodenprobe.",
      quota_hint: "Free-Paket: {used} von {limit} Standortabfragen genutzt",
      quota_exhausted: "Free-Kontingent aufgebraucht — für weitere Abfragen ist ein Upgrade nötig",
    },
    sites: {
      title: "Baustellen",
      subtitle: "Alle Ihre gespeicherten Standorte",
      new: "Neue Baustelle",
      no_sites: "Sie haben noch keine Baustellen angelegt.",
      name: "Name",
      description: "Beschreibung",
      address: "Adresse",
      created: "Erstellt am",
      view: "Anzeigen",
      delete: "Löschen",
      confirm_delete: "Diese Baustelle wirklich löschen?",
    },
    pricing: {
      title: "Einfache, transparente Preise",
      subtitle: "Wählen Sie den Plan, der zu Ihrem Betrieb passt.",
      cta_subscribe: "Plan wählen",
      cta_current: "Ihr aktueller Plan",
      cta_login_first: "Zum Starten anmelden",
      per_month: "/Monat",
      success_title: "Zahlung erfolgreich!",
      success_subtitle: "Ihr Abonnement wurde aktiviert. Willkommen an Bord!",
      pending: "Zahlung wird verarbeitet…",
      go_dashboard: "Zum Dashboard",
      failed: "Zahlung fehlgeschlagen",
    },
    profile: {
      title: "Profil",
      save: "Speichern",
      saved: "Profil aktualisiert",
      current_plan: "Aktueller Plan",
      change_plan: "Plan ändern",
    },
    common: {
      save: "Speichern",
      cancel: "Abbrechen",
      loading: "Lädt…",
      error: "Fehler",
      success: "Erfolgreich",
      back: "Zurück",
    },
  },
  en: {
    nav: {
      features: "Features",
      pricing: "Pricing",
      login: "Sign in",
      register: "Register now",
      dashboard: "Dashboard",
      sites: "Sites",
      query: "Location Query",
      profile: "Profile",
      billing: "Billing",
      logout: "Sign out",
    },
    landing: {
      hero_eyebrow: "Site data for landscape gardeners",
      hero_title: "Location reports in seconds — not hours.",
      hero_subtitle:
        "Climate, soil and hardiness zones for any address in the DACH region. Ready for your next project, ready as a PDF.",
      hero_cta_primary: "Start with fair code",
      hero_cta_secondary: "See features",
      fair_badge: "Exclusive trade fair test",
      fair_note:
        "Access is currently exclusive to trade fair visitors: register with the code from the fair to unlock the Free plan.",
      trust: "Trusted by landscape architects & garden professionals",
      features_title: "Everything you need to know about any site.",
      features_subtitle:
        "From the first site analysis to the client report — all data in one place.",
      f1_title: "10-year climate averages",
      f1_desc: "Precipitation, temperature, sunshine hours and frost days from historic weather data.",
      f2_title: "USDA hardiness zone",
      f2_desc: "Computed from 30 years of ERA5-Land data (~9 km) over the 1991–2020 normal period — official USDA methodology.",
      f3_title: "Soil data ISRIC SoilGrids",
      f3_desc: "USDA texture class, pH, humus, nutrient storage (CEC), coarse fragments, available water — plus bedrock geology.",
      f4_title: "Site management",
      f4_desc: "Save locations with all data — per project, per client, per region.",
      f5_title: "Professional PDF",
      f5_desc:
        "One-click export of the full location report — with your company logo and all charts.",
      f6_title: "Geocoding & map",
      f6_desc: "Address search or GPS coordinates — visualised on an interactive map.",
      cta_title: "Ready for your next site?",
      cta_subtitle: "Exclusive trade fair test — register with your fair code and the Free plan is yours.",
      cta_button: "Get started",
    },
    auth: {
      login_title: "Welcome back",
      login_subtitle: "Sign in to your BaugrundCheck.de account",
      register_title: "Create account",
      register_subtitle: "Exclusive trade fair test — register with your fair code",
      email: "Email",
      password: "Password",
      name: "Name",
      company: "Company (optional)",
      access_code: "Fair code",
      access_code_placeholder: "Code from the fair",
      access_code_hint: "You received the code at our booth. It unlocks the Free plan.",
      login_button: "Sign in",
      register_button: "Create account",
      no_account: "No account yet?",
      have_account: "Already have an account?",
      to_register: "Register now",
      to_login: "Sign in here",
    },
    dashboard: {
      welcome: "Welcome back",
      subtitle: "Your location data overview",
      kpi_sites: "Sites",
      kpi_queries: "Queries",
      kpi_plan: "Current plan",
      cta_query: "New location query",
      cta_sites: "All sites",
      recent: "Recent sites",
      no_sites: "No sites yet.",
      create_first: "Create first site",
      quota_left: "{n} remaining",
      quota_exhausted: "Quota used up",
    },
    query: {
      title: "Location query",
      subtitle: "Enter an address or coordinates to get full site data.",
      tab_address: "Address",
      tab_coords: "Coordinates",
      tab_geo: "My location",
      geo_hint: "Click the button — your browser will ask for location permission.",
      geo_button: "Use current location",
      geo_locating: "Detecting location…",
      geo_denied: "Location access was denied or unavailable.",
      geo_unavailable: "Geolocation is not supported by this browser.",
      address_placeholder: "e.g. 12 Sample Street, Munich",
      lat: "Latitude",
      lng: "Longitude",
      search: "Query",
      save_site: "Save as site",
      download_pdf: "Download PDF report",
      loading: "Loading location data…",
      no_result: "Address not found.",
      section_overview: "Overview",
      section_climate: "Climate",
      section_hardiness: "Hardiness zone",
      section_soil: "Soil",
      section_geology: "Bedrock geology",
      coords: "Coordinates",
      elevation: "Elevation",
      country: "Country",
      state: "Region",
      annual_temp: "Avg. temperature",
      annual_precip: "Precipitation/year",
      annual_sun: "Sunshine hours/year",
      frost_days: "Frost days/year",
      ph: "pH",
      texture: "Texture class",
      sand: "Sand",
      silt: "Silt",
      clay: "Clay",
      organic_carbon: "Organic carbon",
      humus: "Humus",
      cec: "CEC (cmol/kg)",
      nitrogen: "Total nitrogen",
      coarse_fragments: "Coarse fragments",
      field_capacity: "Field capacity",
      available_water: "Available water",
      bulk_density: "Bulk density",
      hardiness_label: "USDA zone",
      hardiness_range: "Temperature range",
      hardiness_min: "Planning value (coldest winters)",
      hardiness_usda: "USDA mean (all years)",
      hardiness_lowest: "Lowest winter temperature",
      hardiness_fallback:
        "Fallback source with coarser grid used — the zone may deviate in complex terrain.",
      source: "Source",
      years: "years",
      lithology: "Lithology",
      geo_unit: "Map unit",
      geo_period: "Age",
      geo_environment: "Depositional setting",
      geo_note: "Note",
      soil_unavailable: "Soil data unavailable",
      association_title: "Soil map unit",
      association_note:
        "Overview map without measured values — it describes the prevailing soil type of the area, but replaces neither gridded data nor a soil sample.",
      quota_hint: "Free plan: {used} of {limit} location queries used",
      quota_exhausted: "Free quota used up — further queries require an upgrade",
    },
    sites: {
      title: "Sites",
      subtitle: "All your saved locations",
      new: "New site",
      no_sites: "You haven't created any sites yet.",
      name: "Name",
      description: "Description",
      address: "Address",
      created: "Created",
      view: "View",
      delete: "Delete",
      confirm_delete: "Really delete this site?",
    },
    pricing: {
      title: "Simple, transparent pricing",
      subtitle: "Choose the plan that fits your business.",
      cta_subscribe: "Choose plan",
      cta_current: "Your current plan",
      cta_login_first: "Sign in to subscribe",
      per_month: "/month",
      success_title: "Payment successful!",
      success_subtitle: "Your subscription is active. Welcome aboard!",
      pending: "Processing payment…",
      go_dashboard: "Go to dashboard",
      failed: "Payment failed",
    },
    profile: {
      title: "Profile",
      save: "Save",
      saved: "Profile updated",
      current_plan: "Current plan",
      change_plan: "Change plan",
    },
    common: {
      save: "Save",
      cancel: "Cancel",
      loading: "Loading…",
      error: "Error",
      success: "Success",
      back: "Back",
    },
  },
};

const I18nCtx = createContext({ t: (k) => k, lang: "de", setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    return localStorage.getItem("baugrundcheck_lang")
      || localStorage.getItem("terrasite_lang")
      || "de";
  });

  useEffect(() => {
    localStorage.setItem("baugrundcheck_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  // vars füllt Platzhalter der Form {name} — z.B. t("dashboard.quota_left", { n: 3 }).
  const t = (key, vars) => {
    const parts = key.split(".");
    let cur = translations[lang];
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) cur = cur[p];
      else return key;
    }
    if (typeof cur !== "string") return key;
    if (!vars) return cur;
    return cur.replace(/\{(\w+)\}/g, (match, name) =>
      vars[name] != null ? String(vars[name]) : match
    );
  };

  return (
    <I18nCtx.Provider value={{ t, lang, setLang: setLangState }}>{children}</I18nCtx.Provider>
  );
}

export const useI18n = () => useContext(I18nCtx);
